/**
 * EntityListShell — full item-list header chrome (§1E).
 * Page header + toolbar + active filters + optional context bar.
 *
 * On a phone the header is two rows — the title, then search, the primary
 * action and a More button — and Refresh, Import, Export and any secondary
 * actions sit in the More sheet (audit 2026-11-02, #46).
 */

import { useState, type ReactNode } from 'react';
import {
  Search,
  X as XIcon,
  RefreshCw,
  Plus,
  ArrowDownToLine,
  ArrowUpFromLine,
  MoreHorizontal,
} from 'lucide-react';
import { FilterBar } from './FilterBar';
import { EntityListToolbar, type EntityListToolbarProps } from './EntityListToolbar';
import { EntityListActiveFilters } from './EntityListActiveFilters';
import type { ActiveFilter, FacetDefinition } from './types';
import { useKeyboardShortcutHints, withoutShortcutHint } from '../../hooks/useKeyboardShortcutHints';
import { useMobileExperience } from '../../mobile/featureFlag';
import { BottomSheet } from '../../mobile/BottomSheet';

export interface EntityListShellProps<TFilter = string> {
  icon: ReactNode;
  iconBgClassName?: string;
  title: string;
  subtitle: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;
  onRefresh?: () => void;
  refreshing?: boolean;
  onImport?: () => void;
  onExport?: () => void;
  primaryAction?: { label: string; onClick: () => void; disabled?: boolean };
  secondaryActions?: ReactNode;
  toolbar?: EntityListToolbarProps;
  hideToolbar?: boolean;
  filterPanelOpen?: boolean;
  facets?: FacetDefinition<TFilter>[];
  activeFilters?: ActiveFilter<TFilter>[];
  onFilterChange?: (filters: ActiveFilter<TFilter>[]) => void;
  onRemoveFilter?: (filterId: string) => void;
  onClearFilters?: () => void;
  contextBar?: { label: string; action?: { label: string; onClick: () => void } };
  error?: string | null;
  headerExtra?: ReactNode;
  /** Inserted in page header before search (e.g. Focus Mode toggle) */
  headerActions?: ReactNode;
  /** Custom filter chip row — when set, skips built-in EntityListActiveFilters */
  afterToolbar?: ReactNode;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
  children: ReactNode;
  className?: string;
}

export function EntityListShell<TFilter = string>({
  icon,
  iconBgClassName = 'bg-[var(--slate-dim)]',
  title,
  subtitle,
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search… ⌘K',
  onRefresh,
  refreshing,
  onImport,
  onExport,
  primaryAction,
  secondaryActions,
  toolbar,
  hideToolbar = false,
  filterPanelOpen = false,
  facets = [],
  activeFilters = [],
  onFilterChange,
  onRemoveFilter,
  onClearFilters,
  contextBar,
  error,
  headerExtra,
  headerActions,
  afterToolbar,
  searchInputRef,
  children,
  className = '',
}: EntityListShellProps<TFilter>) {
  // No ⌘K hint on a phone or a touch screen: there is no key to press.
  const showShortcutHints = useKeyboardShortcutHints();
  const isPhone = useMobileExperience();
  const [moreOpen, setMoreOpen] = useState(false);
  const hasMoreActions = !!(onRefresh || onImport || onExport || secondaryActions);
  const sheetButton =
    'flex min-h-[44px] w-full items-center gap-2 px-3 text-sm text-[var(--text-sec)] active:bg-[var(--hover-tint)] disabled:opacity-50';
  return (
    <div
      className={`h-full flex flex-col bg-[var(--bg-raised)] dark:bg-[var(--surface)]  border border-[var(--rule)]/80 dark:border-[var(--rule)] shadow-sm overflow-hidden ${className}`}
    >
      {/* Page header — matches ItemListView. Wraps on a phone: the title
          keeps its line and the actions and search drop below it. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 px-4 md:px-6 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-[1_1_12rem]">
          <div
            className={`flex items-center justify-center w-8 h-8  shrink-0 ${iconBgClassName}`}
          >
            {icon}
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-tight truncate">
              {title}
            </h1>
            <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)] leading-tight truncate">
              {subtitle}
            </p>
          </div>
        </div>
        <div className={isPhone ? 'flex w-full items-center gap-2' : 'flex max-w-full flex-wrap items-center gap-3'}>
          {headerActions}
          {!isPhone && secondaryActions}
          <div className={isPhone ? 'relative min-w-0 flex-1' : 'relative'}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  onSearchChange('');
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder={showShortcutHints ? searchPlaceholder : withoutShortcutHint(searchPlaceholder)}
              className={`${isPhone ? 'w-full' : 'w-56 max-w-full focus:w-72'} pl-9 pr-8 py-1.5 text-sm bg-[var(--surface)] dark:bg-[var(--surface)] border border-[var(--rule)]/80 dark:border-[var(--rule-bright)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:bg-[var(--bg-raised)] dark:focus:bg-[var(--bg-raised)] focus:border-[var(--slate)] dark:focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 dark:focus:ring-[var(--active-tint)] transition-all duration-150 outline-none`}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {!isPhone && onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="p-2 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          {!isPhone && onImport && (
            <button
              type="button"
              onClick={onImport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule-bright)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] hover:border-[var(--rule)] transition-all"
            >
              <ArrowDownToLine className="h-3.5 w-3.5" />
              Import
            </button>
          )}
          {!isPhone && onExport && (
            <button
              type="button"
              onClick={onExport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule-bright)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] hover:border-[var(--rule)] transition-all"
            >
              <ArrowUpFromLine className="h-3.5 w-3.5" />
              Export
            </button>
          )}
          {primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              className="flex items-center gap-2 px-3.5 py-1.5 bg-[var(--cta)] text-[var(--cta-ink)] text-sm font-semibold hover:bg-[var(--cta-hover)] transition-colors disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              {primaryAction.label}
            </button>
          )}
          {isPhone && hasMoreActions && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-label="More actions"
              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center border border-[var(--rule)] text-[var(--text-sec)] active:bg-[var(--hover-tint)]"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {isPhone && hasMoreActions && (
        <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title={title}>
          <div className="space-y-2 pb-2">
            {onRefresh && (
              <button
                type="button"
                disabled={refreshing}
                onClick={() => {
                  setMoreOpen(false);
                  onRefresh();
                }}
                className={sheetButton}
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            )}
            {onImport && (
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  onImport();
                }}
                className={sheetButton}
              >
                <ArrowDownToLine className="h-4 w-4" />
                Import
              </button>
            )}
            {onExport && (
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  onExport();
                }}
                className={sheetButton}
              >
                <ArrowUpFromLine className="h-4 w-4" />
                Export
              </button>
            )}
            {secondaryActions && <div className="flex flex-wrap items-center gap-2 pt-1">{secondaryActions}</div>}
          </div>
        </BottomSheet>
      )}

      {headerExtra}

      {error && (
        <div className="mx-6 mt-3 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] p-3 text-sm text-[var(--coral)] dark:text-[var(--coral)] flex-shrink-0">
          {error}
        </div>
      )}

      {!hideToolbar && toolbar && <EntityListToolbar {...toolbar} />}

      {filterPanelOpen && facets.length > 0 && onFilterChange && (
        <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)]/40 flex-shrink-0">
          <FilterBar facets={facets} activeFilters={activeFilters} onFilterChange={onFilterChange} />
        </div>
      )}

      {afterToolbar}

      {onRemoveFilter && onClearFilters && !afterToolbar && (
        <EntityListActiveFilters
          activeFilters={activeFilters}
          onRemoveFilter={onRemoveFilter}
          onClearAll={onClearFilters}
        />
      )}

      {contextBar && (
        <div className="flex items-center gap-2 px-4 py-1.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)]/60 dark:bg-[var(--surface-raised)] flex-shrink-0">
          <span className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">{contextBar.label}</span>
          {contextBar.action && (
            <button
              type="button"
              onClick={contextBar.action.onClick}
              className="ml-auto text-[11px] font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
            >
              {contextBar.action.label}
            </button>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col">{children}</div>
    </div>
  );
}
